<template>
  <v-dialog v-model="showModal" max-width="820px" persistent>
    <v-card>
      <v-toolbar dark class="primary">
        <v-btn icon @click.native="close" dark><v-icon>fa-times</v-icon></v-btn>
        <v-toolbar-title>Wyślij dokument na tablet</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pt-4">
        <v-progress-linear :indeterminate="true" v-if="loading"></v-progress-linear>

        <div v-if="!sessionId">
          <div class="subtitle-1 mb-3">Pacjent: <b>{{ clientName }}</b></div>
          <v-alert type="info" dense text v-if="excludedCount > 0">
            {{ excludedCount }} {{ excludedCount === 1 ? 'dokument jest' : 'dokumenty/-ów jest' }} już podpisany/-e przez pacjenta - nie ma go/ich na liście.
          </v-alert>
          <v-row>
            <v-col cols="12" md="7">
              <v-select v-model="templateId" :items="templates" item-value="id" :item-text="templateText"
                label="Dokument" outlined dense :hint="templateHint" persistent-hint></v-select>
            </v-col>
            <v-col cols="12" md="5">
              <v-select v-model="terminalId" :items="terminals" item-value="id" :item-text="terminalText"
                label="Tablet" outlined dense></v-select>
            </v-col>
          </v-row>

          <div class="subtitle-2 mt-2">Podpisujący</div>
          <v-row align="center">
            <v-col cols="12" md="6"><b>{{ clientName }}</b> <span class="grey--text">(pacjent)</span></v-col>
            <v-col cols="12" md="6">
              <v-select v-model="patientIdentity" :items="identityItems" label="Weryfikacja tożsamości" outlined dense hide-details></v-select>
            </v-col>
          </v-row>
          <v-checkbox v-model="withRepresentative" :label="selectedTemplate && selectedTemplate.requiresDualConsent ? 'Drugi podpisujący (zgoda podwójna - wymagany)' : 'Dodaj drugiego podpisującego (przedstawiciel ustawowy / opiekun)'"
            hide-details class="mt-2"></v-checkbox>
          <v-row v-if="withRepresentative" class="mt-1">
            <v-col cols="12" md="4">
              <v-select v-model="rep.role" :items="repRoles" label="Rola" outlined dense hide-details></v-select>
            </v-col>
            <v-col cols="6" md="4"><v-text-field v-model="rep.firstName" label="Imię" outlined dense hide-details></v-text-field></v-col>
            <v-col cols="6" md="4"><v-text-field v-model="rep.lastName" label="Nazwisko" outlined dense hide-details></v-text-field></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="rep.relation" label="Relacja (np. matka, opiekun prawny)" outlined dense hide-details></v-text-field></v-col>
            <v-col cols="12" md="6">
              <v-select v-model="rep.identity" :items="identityItems" label="Weryfikacja tożsamości" outlined dense hide-details></v-select>
            </v-col>
          </v-row>
          <v-alert type="warning" dense text class="mt-3" v-if="selectedTemplate && selectedTemplate.legalFormRequirement === 'WRITTEN'">
            Ten dokument wymaga formy pisemnej - podpis na tablecie nie zastępuje podpisu własnoręcznego.
          </v-alert>
        </div>

        <div v-else>
          <SigningSessionStatus :sessionId="sessionId" @finished="onFinished"></SigningSessionStatus>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">{{ sessionId ? 'Zamknij' : 'Anuluj' }}</v-btn>
        <v-btn color="primary" v-if="!sessionId" :disabled="!canSend" :loading="loading" @click="send">
          <v-icon left small>fa-tablet-alt</v-icon> Wyślij na tablet
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { podpisyService, podpisyLabels, abpError } from "@/services/podpisy.service";
import SigningSessionStatus from "./SigningSessionStatus";
import { mapActions } from "vuex";

export default {
  name: "SendToTabletDialog",
  components: { SigningSessionStatus },
  props: ["visible", "clientId", "clientName", "excludeTemplateCodes"],
  data() {
    return {
      loading: false,
      templates: [],
      terminals: [],
      excludedCount: 0,
      templateId: null,
      terminalId: null,
      patientIdentity: "ID_CARD",
      withRepresentative: false,
      rep: { role: "LEGAL_REPRESENTATIVE", firstName: "", lastName: "", relation: "", identity: "ID_CARD" },
      sessionId: null,
      finished: null,
      identityItems: Object.keys(podpisyLabels.identity).map(k => ({ value: k, text: podpisyLabels.identity[k] })),
    };
  },
  computed: {
    showModal: {
      get() { return this.visible; },
      set(v) { if (!v) this.close(); },
    },
    selectedTemplate() {
      return this.templates.find(t => t.id === this.templateId) || null;
    },
    templateHint() {
      const t = this.selectedTemplate;
      if (!t) return "";
      return "wersja " + t.version + " · " + (podpisyLabels.legalForm[t.legalFormRequirement] || "") + " · " + (podpisyLabels.captureMethod[t.captureMethod] || "");
    },
    repRoles() {
      const allowed = this.selectedTemplate ? (this.selectedTemplate.signerRolesAllowed || "").split(",") : [];
      return ["LEGAL_REPRESENTATIVE", "DE_FACTO_GUARDIAN", "WITNESS"]
        .filter(r => allowed.length === 0 || allowed.indexOf(r) >= 0)
        .map(r => ({ value: r, text: podpisyLabels.signerRole[r] }));
    },
    canSend() {
      if (!this.templateId || !this.terminalId) return false;
      if (this.withRepresentative && (!this.rep.firstName || !this.rep.lastName)) return false;
      return true;
    },
  },
  methods: {
    ...mapActions("alert", ["error", "success"]),
    templateText(t) { return t.title + " (" + t.code + " v" + t.version + ")"; },
    terminalText(t) { return t.code + (t.name ? " - " + t.name : "") + (t.enrolled ? "" : " (niezarejestrowany)"); },
    load() {
      this.loading = true;
      Promise.all([podpisyService.getActiveTemplates(), podpisyService.getTerminals()]).then(([t, term]) => {
        const exclude = this.excludeTemplateCodes || [];
        const all = t.data.result;
        this.templates = all.filter(x => exclude.indexOf(x.code) < 0);
        this.excludedCount = all.length - this.templates.length;
        this.terminals = term.data.result.filter(x => x.active && x.enrolled);
        if (this.templates.length === 1) this.templateId = this.templates[0].id;
        if (this.terminals.length === 1) this.terminalId = this.terminals[0].id;
        if (this.templates.length === 0) {
          this.error(this.excludedCount > 0
            ? "Wszystkie dostępne dokumenty pacjent ma już podpisane"
            : "Brak opublikowanych szablonów dokumentów");
        }
        if (this.terminals.length === 0) this.error("Brak zarejestrowanego tabletu");
      }).catch(e => this.error(abpError(e, "Nie można pobrać szablonów i terminali")))
        .finally(() => { this.loading = false; });
    },
    send() {
      const signers = [{ role: "PATIENT", identityVerification: this.patientIdentity }];
      if (this.withRepresentative) {
        signers.push({ role: this.rep.role, firstName: this.rep.firstName, lastName: this.rep.lastName,
          relation: this.rep.relation, identityVerification: this.rep.identity });
      }
      this.loading = true;
      podpisyService.createSession({ clientId: this.clientId, templateId: this.templateId, terminalId: this.terminalId, signers: signers })
        .then(resp => { this.sessionId = resp.data.result.sessionId; })
        .catch(e => this.error(abpError(e, "Nie można wysłać dokumentu na tablet")))
        .finally(() => { this.loading = false; });
    },
    onFinished(session) {
      this.finished = session;
      if (session.status === "SIGNED") this.success("Dokument " + session.documentNumber + " został podpisany");
    },
    close() {
      this.$emit("close", this.finished);
    },
  },
  watch: {
    selectedTemplate(t) {
      if (t && t.requiresDualConsent) this.withRepresentative = true;
    },
  },
  mounted() {
    this.load();
  },
};
</script>
